import { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';

import lang from '../../lib/language';
import { roundPercentValues } from '../../lib/util/DataHelpers';
import { isEmpty } from '../../lib/util/typeGuards';

import useIsMobile from '../hooks/useIsMobile';
import NoDataAvailable from '../NoDataAvailable/NoDataAvailable';

import { StockColumn } from './components/StockColumn';
import HighGrowthCompaniesInfo from './HighGrowthCompaniesInfo/HighGrowthCompaniesInfo';
import { HighGrowthCompanyRow } from './LongTermInvestments.types';

import styles from './DividendPyersAndHighGrowthCompanies.module.scss';

const MOBILE_COMPANIES_COUNT_LIMIT = 6;
const HOVER_CLEAR_DELAY_MS = 140;

type Props = {
  companiesContent: HighGrowthCompanyRow[];
};

const HighGrowthCompanies = ({ companiesContent }: Props) => {
  const { isMobile } = useIsMobile();
  const [hoveredSymbol, setHoveredSymbol] = useState('');
  const chartPipesRef = useRef<HTMLDivElement>(null);
  const hoverClearTimeoutRef = useRef<number | null>(null);

  const sortedCompanies = useMemo(() => (
    [...companiesContent].sort((a, b) => b.growthIndicator - a.growthIndicator)
  ), [companiesContent]);

  const maxGrowthIndicator = sortedCompanies[0]?.growthIndicator ?? 0;

  const companiesToRender = isMobile
    ? sortedCompanies.slice(0, MOBILE_COMPANIES_COUNT_LIMIT)
    : sortedCompanies;

  const clearHoverTimeout = () => {
    if (hoverClearTimeoutRef.current === null) {
      return;
    }

    window.clearTimeout(hoverClearTimeoutRef.current);
    hoverClearTimeoutRef.current = null;
  };

  const handleHoverElement = (symbol: string) => {
    clearHoverTimeout();

    if (symbol) {
      setHoveredSymbol(symbol);
      return;
    }

    if (isMobile) {
      setHoveredSymbol('');
      return;
    }

    hoverClearTimeoutRef.current = window.setTimeout(() => {
      setHoveredSymbol('');
      hoverClearTimeoutRef.current = null;
    }, HOVER_CLEAR_DELAY_MS);
  };

  useEffect(() => () => {
    clearHoverTimeout();
  }, []);

  if (isEmpty(companiesContent)) {
    return (
      <div className={styles.wrapper}>
        <NoDataAvailable
          className={styles.noDataAvailable}
          isNoDataIcon
          title={lang.commonDiscover52WeekNoDataAll()}
        />
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <p className={styles.dotWrapper}>
        <span className={classNames(styles.activeDot, styles.blueDot)} />
        {lang.commonDiscoverHighGrowthCompaniesLegend()}
        <HighGrowthCompaniesInfo />
      </p>
      <div className={styles.chartPipes} ref={chartPipesRef}>
        {companiesToRender.map(({
          growthIndicator,
          symbol,
          logoUrl,
          currencySymbol,
          revenue,
          marketCap,
          epsLast12Months,
          avg30Volume,
        }, index) => {
          const tooltip = {
            primaryMetric: revenue,
            secondaryMetric: epsLast12Months,
            volume: avg30Volume,
            marketCap,
          };

          return (
            <StockColumn
              key={symbol}
              blur={hoveredSymbol !== symbol && hoveredSymbol !== ''}
              percentageLabel={roundPercentValues(growthIndicator)}
              value={growthIndicator}
              maxValue={maxGrowthIndicator}
              stockLogo={logoUrl}
              symbol={symbol}
              isHighGrowth
              tooltip={tooltip}
              currencySymbol={currencySymbol}
              hovered={hoveredSymbol !== '' && symbol === hoveredSymbol}
              hoverElement={handleHoverElement}
              containerRef={chartPipesRef}
              animationOrder={index}
            />
          );
        })}
      </div>
    </div>
  );
};

export default HighGrowthCompanies;
